import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Shield, TrendingUp, Calendar, AlertCircle, Clock, Edit2, Check, X, Stethoscope, FileText, CheckCircle2, MessageSquare, Plus, Settings, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import AgencyRequiredCredentials from '../components/AgencyRequiredCredentials';
import AgencyOnboarding from '../components/AgencyOnboarding';
import { MentionTextarea } from '../components/MentionTextarea';
import { useUserNames } from '../context/UserNamesContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AgencyDetail() {
  const { agencyId } = useParams();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [visits, setVisits] = useState([]);
  const [stats, setStats] = useState({
    ytd_visits: 0,
    avg_visits_per_week: 0,
    avg_missed_last_6_weeks: 0,
    missed_last_upload: 0,
    avg_late_notes_last_6_weeks: 0,
    late_notes_last_upload: 0
  });
  const [loading, setLoading] = useState(true);
  const [editingState, setEditingState] = useState(false);
  const [stateValue, setStateValue] = useState('');
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressValue, setAddressValue] = useState('');
  const [cityValue, setCityValue] = useState('');
  const [zipValue, setZipValue] = useState('');
  const [editingEmails, setEditingEmails] = useState(false);
  const [invoiceEmails, setInvoiceEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  // Auto-email new credentials to this agency
  const [autoEmailCred, setAutoEmailCred] = useState(false);
  const [credentialEmails, setCredentialEmails] = useState([]);
  const [credEmailTypes, setCredEmailTypes] = useState([]);
  const [newCredEmail, setNewCredEmail] = useState('');
  const [editingCredEmails, setEditingCredEmails] = useState(false);
  const [allCredTypes, setAllCredTypes] = useState([]);
  const [savingCredCfg, setSavingCredCfg] = useState(false);
  const [weeklyChartData, setWeeklyChartData] = useState([]);
  const [disciplineData, setDisciplineData] = useState([]);
  const [weeklyData, setWeeklyData] = useState({ weeks: [], projected: 0 });
  
  // Invoice state
  const [unverifiedInvoices, setUnverifiedInvoices] = useState([]);
  const [verifiedInvoices, setVerifiedInvoices] = useState([]);
  const [verifyingInvoice, setVerifyingInvoice] = useState(null);
  
  // Verification modal state
  const [verifyModal, setVerifyModal] = useState({ open: false, invoice: null });
  const [verifyNotes, setVerifyNotes] = useState('');
  const [submittingVerify, setSubmittingVerify] = useState(false);
  
  // Note modal state (for adding notes without verifying)
  const [noteModal, setNoteModal] = useState({ open: false, invoice: null });
  const [noteText, setNoteText] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);

  // Agency-level notes (timeline of notes attached to this agency)
  const [agencyNotes, setAgencyNotes] = useState([]);
  const [newAgencyNote, setNewAgencyNote] = useState('');
  const [agencyNoteMentions, setAgencyNoteMentions] = useState([]);
  const { resolveName } = useUserNames();
  const [savingAgencyNote, setSavingAgencyNote] = useState(false);

  // Custom fields state
  const [customFields, setCustomFields] = useState([]);
  const [customData, setCustomData] = useState({});
  const [editingLayout, setEditingLayout] = useState(false);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [newFieldForm, setNewFieldForm] = useState({
    name: '', field_type: 'text', description: '', target_tab: 'overview'
  });

  useEffect(() => {
    fetchAgencyData();
    fetchCustomFields();
    // Load the master list of credential/document types for the picker.
    axios.get(`${API}/credentials/types`)
      .then(res => setAllCredTypes(res.data?.types || []))
      .catch(() => setAllCredTypes([]));
  }, [agencyId]);

  const fetchAgencyData = async () => {
    try {
      const [agencyRes, visitsRes, notesRes] = await Promise.all([
        axios.get(`${API}/agency-profiles/${agencyId}`),
        axios.get(`${API}/visits/by-agency/${agencyId}`),
        axios.get(`${API}/agency-profiles/${agencyId}/notes`).catch(() => ({ data: [] })),
      ]);
      
      setAgency(agencyRes.data);
      setStateValue(agencyRes.data.state || '');
      setPhoneValue(agencyRes.data.phone || '');
      setAddressValue(agencyRes.data.address || '');
      setCityValue(agencyRes.data.city || '');
      setZipValue(agencyRes.data.zip_code || '');
      setInvoiceEmails(agencyRes.data.invoice_emails || []);
      setAutoEmailCred(!!agencyRes.data.auto_email_credentials);
      setCredentialEmails(agencyRes.data.credential_emails || []);
      setCredEmailTypes(agencyRes.data.credential_email_types || []);
      setVisits(visitsRes.data);
      setAgencyNotes(notesRes.data || []);
      
      // Calculate statistics
      calculateStats(visitsRes.data);
      
      // Calculate weekly chart progress data
      calculateWeeklyChartData(visitsRes.data);
      
      // Calculate discipline breakdown
      calculateDisciplineData(visitsRes.data);
      
      // Calculate weekly summary data
      await calculateWeeklyData(visitsRes.data, agencyRes.data.agency_name);
    } catch (error) {
      toast.error('Failed to load agency data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/custom-fields?target_page=agency`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomFields(res.data || []);
    } catch (error) {
      console.error('Failed to load custom fields:', error);
    }
  };

  const fetchCustomData = async (recordId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/custom-field-values/agency/${recordId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomData(res.data || {});
    } catch (error) {
      console.error('Failed to load custom data:', error);
    }
  };

  // Fetch custom data when agency loads
  useEffect(() => {
    if (agency?.id) {
      fetchCustomData(agency.id);
    }
  }, [agency?.id]);

  const saveCustomData = async (key, value) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/custom-field-values/agency/${agencyId}`,
        { [key]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomData(prev => ({ ...prev, [key]: value }));
    } catch (error) {
      toast.error('Failed to save custom field');
    }
  };

  const handleAddCustomField = async () => {
    if (!newFieldForm.name) { toast.error('Field name is required'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/custom-fields`, {
        ...newFieldForm,
        target_page: 'agency',
        target_tab: 'overview',
        source: 'manual'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Custom field added');
      setShowAddFieldModal(false);
      setNewFieldForm({ name: '', field_type: 'text', description: '', target_tab: 'overview' });
      fetchCustomFields();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create field');
    }
  };

  const handleRemoveCustomField = async (fieldId) => {
    if (!window.confirm('Remove this custom field? Data will be preserved.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/custom-fields/${fieldId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Field removed');
      fetchCustomFields();
    } catch (error) {
      toast.error('Failed to remove field');
    }
  };
  
  const fetchInvoices = async () => {
    try {
      // Fetch all invoices for this agency by ID and name
      const response = await axios.get(`${API}/invoices`, {
        params: { 
          agency_id: agencyId,
          agency_name: agency?.agency_name 
        }
      });
      
      const allInvoices = response.data || [];
      setUnverifiedInvoices(allInvoices.filter(inv => !inv.verified));
      setVerifiedInvoices(allInvoices.filter(inv => inv.verified));
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };
  
  // Fetch invoices when agency data is loaded
  useEffect(() => {
    if (agency?.agency_name) {
      fetchInvoices();
    }
  }, [agency?.agency_name, agencyId]);
  
  const handleVerifyInvoice = async (invoiceId) => {
    setVerifyingInvoice(invoiceId);
    try {
      await axios.post(`${API}/invoices/verify/${invoiceId}`);
      toast.success('Invoice verified');
      fetchInvoices(); // Refresh the list
    } catch (error) {
      toast.error('Failed to verify invoice');
    } finally {
      setVerifyingInvoice(null);
    }
  };
  
  const openVerifyModal = (invoice) => {
    setVerifyModal({ open: true, invoice });
    setVerifyNotes('');
  };

  const closeVerifyModal = () => {
    setVerifyModal({ open: false, invoice: null });
    setVerifyNotes('');
  };

  const submitVerification = async () => {
    if (!verifyModal.invoice) return;
    
    setSubmittingVerify(true);
    try {
      await axios.post(`${API}/invoices/verify/${verifyModal.invoice.id}`, {
        notes: verifyNotes.trim() || null
      });
      toast.success('Invoice verified');
      closeVerifyModal();
      fetchInvoices();
    } catch (error) {
      console.error('Error verifying invoice:', error);
      toast.error('Failed to verify invoice');
    } finally {
      setSubmittingVerify(false);
    }
  };
  
  // Note modal functions
  const openNoteModal = (invoice) => {
    setNoteModal({ open: true, invoice });
    setNoteText(invoice.verification_notes || '');
  };

  const closeNoteModal = () => {
    setNoteModal({ open: false, invoice: null });
    setNoteText('');
  };

  const submitNote = async () => {
    if (!noteModal.invoice) return;
    
    setSubmittingNote(true);
    try {
      await axios.post(`${API}/invoices/${noteModal.invoice.id}/notes`, {
        notes: noteText.trim()
      });
      toast.success('Note saved');
      closeNoteModal();
      fetchInvoices();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Failed to save note');
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleUnverifyInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to unverify this invoice?')) {
      return;
    }
    
    try {
      await axios.post(`${API}/invoices/unverify/${invoiceId}`);
      toast.success('Invoice unverified');
      fetchInvoices();
    } catch (error) {
      console.error('Error unverifying invoice:', error);
      toast.error('Failed to unverify invoice');
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateStats = (visitsData) => {
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const sixWeeksAgo = new Date(now.getTime() - 42 * 24 * 60 * 60 * 1000);
    
    // YTD visits
    const ytdVisits = visitsData.filter(v => {
      const visitDate = new Date(v.date || v.created_at);
      return visitDate >= yearStart;
    }).length;
    
    // Get visits in last 6 weeks for calculations
    const last6WeeksVisits = visitsData.filter(v => {
      const visitDate = new Date(v.date || v.created_at);
      return visitDate >= sixWeeksAgo;
    });
    
    // Calculate average visits per week
    const weekBuckets = {};
    last6WeeksVisits.forEach(visit => {
      const visitDate = new Date(visit.date || visit.created_at);
      const sunday = new Date(visitDate);
      sunday.setDate(visitDate.getDate() - visitDate.getDay());
      const weekKey = sunday.toISOString().split('T')[0];
      weekBuckets[weekKey] = (weekBuckets[weekKey] || 0) + 1;
    });
    const avgVisitsPerWeek = Object.keys(weekBuckets).length > 0 
      ? Object.values(weekBuckets).reduce((a, b) => a + b, 0) / Object.keys(weekBuckets).length 
      : 0;
    
    // Calculate missed visits (status contains 'missed' or 'MV')
    const isMissed = (status) => {
      const s = (status || '').toLowerCase();
      return s.includes('missed') || s.includes('mv') || s.includes('no-show') || s.includes('no show');
    };
    
    const missedWeekBuckets = {};
    last6WeeksVisits.forEach(visit => {
      if (isMissed(visit.status)) {
        const visitDate = new Date(visit.date || visit.created_at);
        const sunday = new Date(visitDate);
        sunday.setDate(visitDate.getDate() - visitDate.getDay());
        const weekKey = sunday.toISOString().split('T')[0];
        missedWeekBuckets[weekKey] = (missedWeekBuckets[weekKey] || 0) + 1;
      }
    });
    const avgMissedLast6Weeks = Object.keys(missedWeekBuckets).length > 0
      ? Object.values(missedWeekBuckets).reduce((a, b) => a + b, 0) / Object.keys(missedWeekBuckets).length
      : 0;
    
    // Get last upload visits (most recent created_at date)
    const sortedByCreated = [...visitsData].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const lastUploadDate = sortedByCreated[0]?.created_at;
    const lastUploadVisits = visitsData.filter(v => v.created_at === lastUploadDate);
    
    const missedLastUpload = lastUploadVisits.filter(v => isMissed(v.status)).length;
    
    // Calculate late notes (status is NOT complete, missed, or MV)
    const isLateNote = (status) => {
      const s = (status || '').toLowerCase();
      return !s.includes('complete') && !s.includes('missed') && !s.includes('mv');
    };
    
    const lateNotesWeekBuckets = {};
    last6WeeksVisits.forEach(visit => {
      if (isLateNote(visit.status)) {
        const visitDate = new Date(visit.date || visit.created_at);
        const sunday = new Date(visitDate);
        sunday.setDate(visitDate.getDate() - visitDate.getDay());
        const weekKey = sunday.toISOString().split('T')[0];
        lateNotesWeekBuckets[weekKey] = (lateNotesWeekBuckets[weekKey] || 0) + 1;
      }
    });
    const avgLateNotesLast6Weeks = Object.keys(lateNotesWeekBuckets).length > 0
      ? Object.values(lateNotesWeekBuckets).reduce((a, b) => a + b, 0) / Object.keys(lateNotesWeekBuckets).length
      : 0;
    
    const lateNotesLastUpload = lastUploadVisits.filter(v => isLateNote(v.status)).length;
    
    setStats({
      ytd_visits: ytdVisits,
      avg_visits_per_week: avgVisitsPerWeek,
      avg_missed_last_6_weeks: avgMissedLast6Weeks,
      missed_last_upload: missedLastUpload,
      avg_late_notes_last_6_weeks: avgLateNotesLast6Weeks,
      late_notes_last_upload: lateNotesLastUpload
    });
  };

  const calculateWeeklyChartData = (visitsData) => {
    const weekBuckets = {};
    const now = new Date();
    
    // Excluded task names (non-billable visits that shouldn't count)
    const excludedTaskNames = [
      'case conference - nb', 'case conference - nb (sn)', 
      'oasis-e1 discharge (non-billable) - pt', 'oasis-e1 discharge (non-billable) - ot',
      'oasis-e1 discharge (non-billable) - st', 'case conference - nb (pt)',
      'case conference - nb (ot)', 'case conference - nb (st)'
    ];
    
    // Helper to determine visit weight (matches report logic)
    const getVisitWeight = (visit) => {
      const taskName = (visit.task_name || '').toLowerCase();
      const status = (visit.status || '').toLowerCase();
      if (status.includes('missed') || status.includes('mv')) return 0;
      if (excludedTaskNames.includes(taskName)) return 0;
      if (taskName.includes('oasis')) {
        if (taskName.includes('nb') || taskName.includes('non-billable')) return 0;
        return 2;
      }
      return 1;
    };
    
    // Calculate the most recent completed Saturday (same logic as report)
    const dayOfWeek = now.getDay(); // 0=Sun, 6=Sat
    const daysBack = dayOfWeek === 6 ? 7 : dayOfWeek + 1;
    const prevSaturday = new Date(now);
    prevSaturday.setDate(now.getDate() - daysBack);
    prevSaturday.setHours(23, 59, 59, 999);
    
    // Initialize last 52 weeks ending on Saturdays (going backwards from prev Saturday)
    const weeks = [];
    for (let i = 51; i >= 0; i--) {
      const saturday = new Date(prevSaturday);
      saturday.setDate(prevSaturday.getDate() - (i * 7));
      saturday.setHours(23, 59, 59, 999);
      const sunday = new Date(saturday);
      sunday.setDate(saturday.getDate() - 6);
      sunday.setHours(0, 0, 0, 0);
      
      const key = `${saturday.getFullYear()}-${String(saturday.getMonth() + 1).padStart(2, '0')}-${String(saturday.getDate()).padStart(2, '0')}`;
      weeks.push({
        key,
        saturday,
        sunday,
        visits: 0,
        completed: 0,
        missed: 0
      });
    }
    
    visitsData.forEach(v => {
      if (!v.date) return;
      let dateStr = v.date;
      let visitDate;
      
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        // MM/DD/YYYY format
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        visitDate = new Date(year, month - 1, day, 12, 0, 0);
      } else {
        // Try ISO format
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          visitDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12, 0, 0);
        } else {
          return;
        }
      }
      
      if (isNaN(visitDate.getTime())) return;
      
      // Find the week this visit belongs to (same logic as report)
      for (let idx = 0; idx < weeks.length; idx++) {
        if (visitDate >= weeks[idx].sunday && visitDate <= weeks[idx].saturday) {
          const weight = getVisitWeight(v);
          weeks[idx].visits += weight;
          
          const status = (v.status || '').toLowerCase();
          if (status.includes('complete') && weight > 0) {
            weeks[idx].completed += weight;
          } else if (status.includes('missed') || status.includes('mv')) {
            weeks[idx].missed++;
          }
          break;
        }
      }
    });
    
    // Convert to chart data format
    const sortedData = weeks.map(w => ({
      week: w.key,
      visits: w.visits,
      completed: w.completed,
      missed: w.missed
    }));
    
    setWeeklyChartData(sortedData);
  };

  const calculateDisciplineData = (visitsData) => {
    const disciplineCounts = {};
    
    visitsData.forEach(v => {
      const taskType = v.task_type || v.task_name || 'Unknown';
      // Extract discipline from task type (e.g., "PT" from "PT Visit", "SN" from "SN Visit")
      let discipline = taskType;
      if (taskType.includes(' ')) {
        discipline = taskType.split(' ')[0];
      }
      disciplineCounts[discipline] = (disciplineCounts[discipline] || 0) + 1;
    });
    
    const sorted = Object.entries(disciplineCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    
    setDisciplineData(sorted);
  };

  const calculateWeeklyData = async (visitsData, agencyName) => {
    // Task names to exclude
    const excludedTaskNames = [
      'discharge summary (pt)', 'discharge summary (ot)', 'discharge summary (st)',
      'nb - supervisory visit (pt)', 'nb - supervisory visit (ot)', 'nb hha sup visit (pt)',
      '5 day discharge notice ot', '5 day discharge notice pt', '5 day discharge notice st',
      'oasis-e1 discharge (non-billable) - ot', 'oasis-e1 discharge (non-billable) - pt',
      'oasis-e1 discharge (non-billable) - st', 'case conference - nb (pt)',
      'case conference - nb (ot)', 'case conference - nb (st)'
    ];
    
    const getVisitWeight = (visit) => {
      const taskName = (visit.task_name || '').toLowerCase();
      const status = (visit.status || '').toLowerCase();
      if (status.includes('missed') || status.includes('mv')) return 0;
      if (excludedTaskNames.includes(taskName)) return 0;
      if (taskName.includes('oasis')) {
        if (taskName.includes('nb') || taskName.includes('non-billable')) return 0;
        return 2;
      }
      return 1;
    };
    
    // Calculate last 6 weeks
    const now = new Date();
    const weeks = [];
    const prevSaturday = new Date(now);
    const dayOfWeek = now.getDay();
    const daysBack = dayOfWeek === 6 ? 7 : dayOfWeek + 1;
    prevSaturday.setDate(now.getDate() - daysBack);
    
    for (let i = 5; i >= 0; i--) {
      const saturday = new Date(prevSaturday);
      saturday.setDate(prevSaturday.getDate() - (i * 7));
      const sunday = new Date(saturday);
      sunday.setDate(saturday.getDate() - 6);
      weeks.push({
        saturday,
        sunday,
        label: `${(saturday.getMonth() + 1).toString().padStart(2, '0')}/${saturday.getDate().toString().padStart(2, '0')}`,
        count: 0
      });
    }
    
    // Count visits per week
    visitsData.forEach(visit => {
      const weight = getVisitWeight(visit);
      if (weight === 0) return;
      
      let visitDate;
      const dateStr = visit.date || visit.created_at;
      if (dateStr && dateStr.includes('/')) {
        const [month, day, year] = dateStr.split('/');
        visitDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
      } else {
        visitDate = new Date(dateStr);
      }
      
      for (let idx = 0; idx < weeks.length; idx++) {
        if (visitDate >= weeks[idx].sunday && visitDate <= weeks[idx].saturday) {
          weeks[idx].count += weight;
          break;
        }
      }
    });
    
    // Get projected count for this agency
    let projected = 0;
    try {
      const projRes = await axios.get(`${API}/projections`);
      const currentSaturday = new Date(now);
      const dayOfWeek = now.getDay(); // 0=Sunday, 6=Saturday
      const daysUntilSat = (6 - dayOfWeek + 7) % 7; // Days until next Saturday
      if (daysUntilSat === 0) {
        // Today is Saturday, use today
        currentSaturday.setDate(now.getDate());
      } else {
        currentSaturday.setDate(now.getDate() + daysUntilSat);
      }
      const weekEnding = currentSaturday.toISOString().split('T')[0];
      
      projRes.data.forEach(proj => {
        if (proj.projection_type === 'Proj 1' && proj.week_ending === weekEnding) {
          const projAgency = (proj.agency || '').toLowerCase();
          if (projAgency === agencyName.toLowerCase()) {
            (proj.visit_details || []).forEach(v => {
              const taskName = (v.visit_name || '').toLowerCase();
              if (excludedTaskNames.includes(taskName)) return;
              if (taskName.includes('oasis')) {
                if (!taskName.includes('nb') && !taskName.includes('non-billable')) projected += 2;
              } else {
                projected += 1;
              }
            });
          }
        }
      });
    } catch (e) {
      console.error('Error fetching projections:', e);
    }
    
    setWeeklyData({ weeks, projected });
  };

  const handleUpdateState = async () => {
    try {
      const response = await axios.put(`${API}/agency-profiles/${agencyId}`, {
        state: stateValue
      });
      setAgency(response.data);
      setEditingState(false);
      toast.success('State updated successfully');
    } catch (error) {
      toast.error('Failed to update state');
    }
  };

  const handleUpdatePhone = async () => {
    try {
      const response = await axios.put(`${API}/agency-profiles/${agencyId}`, {
        phone: phoneValue
      });
      setAgency(response.data);
      setEditingPhone(false);
      toast.success('Phone updated successfully');
    } catch (error) {
      toast.error('Failed to update phone');
    }
  };

  const handleUpdateAddress = async () => {
    try {
      const response = await axios.put(`${API}/agency-profiles/${agencyId}`, {
        address: addressValue,
        city: cityValue,
        zip_code: zipValue,
      });
      setAgency(response.data);
      setEditingAddress(false);
      toast.success('Address updated successfully');
    } catch (error) {
      toast.error('Failed to update address');
    }
  };


  // ----- Agency Notes (timeline) -----
  const handleAddAgencyNote = async () => {
    const text = newAgencyNote.trim();
    if (!text) {
      toast.error('Please type a note first');
      return;
    }
    setSavingAgencyNote(true);
    try {
      const userEmail = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}').email || 'Admin'; }
        catch { return 'Admin'; }
      })();
      const res = await axios.post(`${API}/agency-profiles/${agencyId}/notes`, {
        content: text,
        author: userEmail,
        mentions: agencyNoteMentions,
      });
      setAgencyNotes(prev => [...prev, res.data]);
      setNewAgencyNote('');
      setAgencyNoteMentions([]);
      toast.success('Note added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setSavingAgencyNote(false);
    }
  };

  const handleDeleteAgencyNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/agency-profiles/${agencyId}/notes/${noteId}`);
      setAgencyNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleUpdatePaymentType = async (newType) => {
    if (newType !== 'Check' && newType !== 'ACH') return;
    if (newType === agency?.payment_type) return;
    try {
      const response = await axios.put(`${API}/agency-profiles/${agencyId}`, {
        payment_type: newType
      });
      setAgency(response.data);
      toast.success(`Payment type set to ${newType}`);
    } catch (error) {
      toast.error('Failed to update payment type');
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim() || !newEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    const updatedEmails = [...invoiceEmails, newEmail.trim()];
    try {
      const response = await axios.put(`${API}/agency-profiles/${agencyId}`, {
        invoice_emails: updatedEmails
      });
      setAgency(response.data);
      setInvoiceEmails(updatedEmails);
      setNewEmail('');
      toast.success('Email added');
    } catch (error) {
      toast.error('Failed to add email');
    }
  };

  const handleRemoveEmail = async (emailToRemove) => {
    const updatedEmails = invoiceEmails.filter(e => e !== emailToRemove);
    try {
      const response = await axios.put(`${API}/agency-profiles/${agencyId}`, {
        invoice_emails: updatedEmails
      });
      setAgency(response.data);
      setInvoiceEmails(updatedEmails);
      toast.success('Email removed');
    } catch (error) {
      toast.error('Failed to remove email');
    }
  };

  // --- Auto-email new credentials -----------------------------------------
  const saveCredConfig = async (patch, successMsg) => {
    setSavingCredCfg(true);
    try {
      const response = await axios.put(`${API}/agency-profiles/${agencyId}`, patch);
      setAgency(response.data);
      setAutoEmailCred(!!response.data.auto_email_credentials);
      setCredentialEmails(response.data.credential_emails || []);
      setCredEmailTypes(response.data.credential_email_types || []);
      if (successMsg) toast.success(successMsg);
    } catch (error) {
      toast.error('Failed to save credential email settings');
    } finally {
      setSavingCredCfg(false);
    }
  };

  const handleToggleAutoEmail = async () => {
    await saveCredConfig(
      { auto_email_credentials: !autoEmailCred },
      !autoEmailCred ? 'Auto-email enabled' : 'Auto-email disabled'
    );
  };

  const handleAddCredEmail = async () => {
    if (!newCredEmail.trim() || !newCredEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    const updated = [...credentialEmails, newCredEmail.trim()];
    setNewCredEmail('');
    await saveCredConfig({ credential_emails: updated }, 'Email added');
  };

  const handleRemoveCredEmail = async (emailToRemove) => {
    const updated = credentialEmails.filter(e => e !== emailToRemove);
    await saveCredConfig({ credential_emails: updated }, 'Email removed');
  };

  const handleToggleCredType = async (type) => {
    const has = credEmailTypes.includes(type);
    const updated = has ? credEmailTypes.filter(t => t !== type) : [...credEmailTypes, type];
    await saveCredConfig({ credential_email_types: updated });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Loading agency...</div>
      </div>
    );
  }

  if (!agency) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Agency not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/agencies')}
          className="gap-2"
          data-testid="back-to-agencies"
        >
          <ArrowLeft size={18} />
          Back to Agencies
        </Button>
        <div className="flex-1" />
        <Button
          variant={editingLayout ? "default" : "ghost"}
          size="sm"
          onClick={() => setEditingLayout(!editingLayout)}
          data-testid="agency-edit-layout-btn"
          title="Add or remove custom fields on this page"
        >
          <Settings size={14} className="mr-1" />
          {editingLayout ? 'Done' : 'Layout'}
        </Button>
      </div>

      <div className="flex items-start gap-6">
        <div className="p-4 bg-primary/10 rounded-xl">
          <Shield className="text-primary" size={48} strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
            {agency.agency_name}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <p className="text-muted-foreground leading-relaxed">
              Agency profile created on {new Date(agency.created_at).toLocaleDateString()}
            </p>
            <button
              onClick={async () => {
                const newActive = !(agency.is_active !== false);
                try {
                  await axios.put(`${API}/agency-profiles/${agencyId}`, { is_active: newActive });
                  setAgency({ ...agency, is_active: newActive });
                  toast.success(newActive ? 'Agency marked active' : 'Agency marked inactive');
                } catch (e) {
                  toast.error('Failed to update active status');
                }
              }}
              className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${
                agency.is_active === false
                  ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                  : 'bg-green-100 text-green-800 hover:bg-green-200'
              }`}
              data-testid="toggle-agency-active-btn"
              title="Click to toggle active status"
            >
              {agency.is_active === false ? 'Inactive' : 'Active'}
            </button>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">State:</span>
            {editingState ? (
              <div className="flex items-center gap-2">
                <Input
                  value={stateValue}
                  onChange={(e) => setStateValue(e.target.value)}
                  className="w-24 h-8"
                  placeholder="TX"
                  data-testid="agency-state-input"
                />
                <Button
                  size="sm"
                  onClick={handleUpdateState}
                  className="h-8 w-8 p-0"
                  data-testid="save-agency-state"
                >
                  <Check size={16} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingState(false);
                    setStateValue(agency.state || '');
                  }}
                  className="h-8 w-8 p-0"
                >
                  <X size={16} />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{agency.state || 'Not set'}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditingState(true)}
                  className="h-6 w-6 p-0"
                  data-testid="edit-agency-state"
                >
                  <Edit2 size={14} />
                </Button>
              </div>
            )}
          </div>

          {/* Address (for map) */}
          <div className="mt-2 flex items-start gap-2">
            <span className="text-sm font-medium text-muted-foreground pt-1">Address:</span>
            {editingAddress ? (
              <div className="flex flex-col gap-2">
                <Input value={addressValue} onChange={(e) => setAddressValue(e.target.value)} className="w-64 h-8" placeholder="123 Main St" data-testid="agency-address-input" />
                <div className="flex items-center gap-2">
                  <Input value={cityValue} onChange={(e) => setCityValue(e.target.value)} className="w-32 h-8" placeholder="City" data-testid="agency-city-input" />
                  <Input value={zipValue} onChange={(e) => setZipValue(e.target.value)} className="w-24 h-8" placeholder="ZIP" data-testid="agency-zip-input" />
                  <Button size="sm" onClick={handleUpdateAddress} className="h-8 w-8 p-0" data-testid="save-agency-address"><Check size={16} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditingAddress(false); setAddressValue(agency.address || ''); setCityValue(agency.city || ''); setZipValue(agency.zip_code || ''); }} className="h-8 w-8 p-0"><X size={16} /></Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">
                  {[agency.address, agency.city, agency.zip_code].filter(Boolean).join(', ') || 'Not set'}
                </span>
                <Button size="sm" variant="ghost" onClick={() => setEditingAddress(true)} className="h-6 w-6 p-0" data-testid="edit-agency-address"><Edit2 size={14} /></Button>
              </div>
            )}
          </div>

          {/* Phone */}
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">Phone:</span>
            {editingPhone ? (
              <div className="flex items-center gap-2">
                <Input
                  value={phoneValue}
                  onChange={(e) => setPhoneValue(e.target.value)}
                  className="w-40 h-8"
                  placeholder="(555) 555-5555"
                  data-testid="agency-phone-input"
                />
                <Button
                  size="sm"
                  onClick={handleUpdatePhone}
                  className="h-8 w-8 p-0"
                >
                  <Check size={16} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingPhone(false);
                    setPhoneValue(agency.phone || '');
                  }}
                  className="h-8 w-8 p-0"
                >
                  <X size={16} />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{agency.phone || 'Not set'}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditingPhone(true)}
                  className="h-6 w-6 p-0"
                >
                  <Edit2 size={14} />
                </Button>
              </div>
            )}
          </div>

          {/* Payment Type */}
          <div className="mt-2 flex items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">Payment Type:</span>
            <div className="flex items-center gap-3" data-testid="agency-payment-type">
              {['Check', 'ACH'].map(opt => {
                const active = (agency?.payment_type || 'Check') === opt;
                return (
                  <label
                    key={opt}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md border cursor-pointer text-sm transition-colors ${
                      active
                        ? 'bg-primary/10 border-primary text-primary font-medium'
                        : 'bg-background border-input hover:bg-muted'
                    }`}
                    data-testid={`payment-type-${opt.toLowerCase()}`}
                  >
                    <input
                      type="radio"
                      name="agency-payment-type"
                      checked={active}
                      onChange={() => handleUpdatePaymentType(opt)}
                      className="h-3.5 w-3.5"
                    />
                    {opt}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Invoice Emails */}
          <div className="mt-2">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium text-muted-foreground">Invoice Emails:</span>
              {!editingEmails && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditingEmails(true)}
                  className="h-6 w-6 p-0"
                >
                  <Edit2 size={14} />
                </Button>
              )}
            </div>
            {invoiceEmails.length === 0 && !editingEmails && (
              <span className="text-sm text-muted-foreground">No invoice emails set</span>
            )}
            {invoiceEmails.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {invoiceEmails.map((email, idx) => (
                  <span 
                    key={idx} 
                    className="inline-flex items-center gap-1 bg-muted px-2 py-0.5 rounded text-sm"
                  >
                    {email}
                    {editingEmails && (
                      <button
                        onClick={() => handleRemoveEmail(email)}
                        className="text-muted-foreground hover:text-red-500"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
            {editingEmails && (
              <div className="flex items-center gap-2 mt-2">
                <Input
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-60 h-8"
                  placeholder="email@example.com"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                />
                <Button size="sm" onClick={handleAddEmail} className="h-8">
                  Add
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setEditingEmails(false)}
                  className="h-8"
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Auto-email New Credentials */}
      <Card className="p-4" data-testid="agency-auto-credential-email-card">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold flex items-center gap-2">
              <Mail size={18} className="text-primary" />
              Auto-email New Credentials
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              When a therapist who sees for this agency gets a new (current) credential,
              automatically email the PDF to the addresses below — but only for the
              credential types you select.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={autoEmailCred}
            disabled={savingCredCfg}
            onClick={handleToggleAutoEmail}
            data-testid="auto-email-credentials-toggle"
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
              autoEmailCred ? 'bg-primary' : 'bg-muted-foreground/30'
            } ${savingCredCfg ? 'opacity-60' : ''}`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                autoEmailCred ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {autoEmailCred && (
          <div className="mt-4 space-y-5" data-testid="auto-email-credentials-config">
            {/* Recipient emails */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-muted-foreground">Credentials Email(s):</span>
                {!editingCredEmails && (
                  <Button size="sm" variant="ghost" onClick={() => setEditingCredEmails(true)} className="h-6 w-6 p-0" data-testid="edit-credential-emails">
                    <Edit2 size={14} />
                  </Button>
                )}
              </div>
              {credentialEmails.length === 0 && !editingCredEmails && (
                <span className="text-sm text-amber-600">No credential emails set — add one so emails can be sent.</span>
              )}
              {credentialEmails.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {credentialEmails.map((email, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 bg-muted px-2 py-0.5 rounded text-sm" data-testid={`credential-email-chip-${idx}`}>
                      {email}
                      {editingCredEmails && (
                        <button onClick={() => handleRemoveCredEmail(email)} className="text-muted-foreground hover:text-red-500">
                          <X size={12} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
              {editingCredEmails && (
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    value={newCredEmail}
                    onChange={(e) => setNewCredEmail(e.target.value)}
                    className="w-60 h-8"
                    placeholder="credentials@agency.com"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCredEmail()}
                    data-testid="new-credential-email-input"
                  />
                  <Button size="sm" onClick={handleAddCredEmail} className="h-8" data-testid="add-credential-email-btn">Add</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingCredEmails(false)} className="h-8">Done</Button>
                </div>
              )}
            </div>

            {/* Credential types to send */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Which credentials to auto-send:
                </span>
                <span className="text-xs text-muted-foreground">{credEmailTypes.length} selected</span>
              </div>
              {allCredTypes.length === 0 ? (
                <span className="text-sm text-muted-foreground">Loading credential types…</span>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5" data-testid="credential-type-picker">
                  {allCredTypes.map((type) => {
                    const checked = credEmailTypes.includes(type);
                    return (
                      <label
                        key={type}
                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border cursor-pointer text-sm transition-colors ${
                          checked ? 'bg-primary/10 border-primary text-primary font-medium' : 'bg-background border-input hover:bg-muted'
                        } ${savingCredCfg ? 'opacity-70' : ''}`}
                        data-testid={`credential-type-option-${type}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={savingCredCfg}
                          onChange={() => handleToggleCredType(type)}
                          className="h-3.5 w-3.5"
                        />
                        <span className="truncate">{type}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {credEmailTypes.length === 0 && allCredTypes.length > 0 && (
                <p className="text-xs text-amber-600 mt-2">
                  No types selected — nothing will be auto-sent until you pick at least one.
                </p>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Agency Notes — feeds the "Agency Notes" notification rule */}
      <Card className="p-4" data-testid="agency-notes-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <MessageSquare size={18} className="text-primary" />
            Notes
            <span className="text-xs text-muted-foreground font-normal">
              ({agencyNotes.length}) — feeds the "Agency Notes" notification rule
            </span>
          </h3>
        </div>

        {/* Add note */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <MentionTextarea
            value={newAgencyNote}
            onChange={setNewAgencyNote}
            mentions={agencyNoteMentions}
            onMentionsChange={setAgencyNoteMentions}
            placeholder="Add a note about this agency… type @ to tag a teammate (Cmd/Ctrl+Enter to save)"
            rows={5}
            testId="agency-note"
            onSubmitShortcut={handleAddAgencyNote}
          />
          <Button
            onClick={handleAddAgencyNote}
            disabled={savingAgencyNote || !newAgencyNote.trim()}
            className="sm:self-start gap-1"
            data-testid="add-agency-note-btn"
          >
            <Plus size={14} />
            {savingAgencyNote ? 'Saving…' : 'Add Note'}
          </Button>
        </div>

        {/* Note timeline */}
        {agencyNotes.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No notes yet. Add one above — recipients on the "Agency Notes" rule will be emailed automatically.
          </p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="agency-notes-list">
            {[...agencyNotes].reverse().map(note => (
              <div
                key={note.id}
                className="border rounded-md p-3 bg-muted/20 hover:bg-muted/30 transition-colors"
                data-testid={`agency-note-${note.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm whitespace-pre-wrap break-words">{note.content}</p>
                    {note.mentions?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {note.mentions.map((m) => (
                          <span key={m.user_id} className="inline-flex items-center gap-0.5 text-[11px] font-medium text-blue-700 bg-blue-50 rounded-full px-1.5 py-0.5">
                            @{m.name}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-muted-foreground mt-1">
                      <span className="font-medium">{resolveName(note.author) || 'Admin'}</span>
                      {note.created_at && (
                        <> · {new Date(note.created_at).toLocaleString()}</>
                      )}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteAgencyNote(note.id)}
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive shrink-0"
                    data-testid={`delete-agency-note-${note.id}`}
                  >
                    <X size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Combined Weekly Summary + Stats - All in One Row */}
      <Card className="p-4">
        <div className="flex items-center gap-6 overflow-x-auto">
          {/* Weekly Visit Counts */}
          <div className="flex items-center gap-2 shrink-0">
            {weeklyData.weeks.map((week, idx) => (
              <div key={idx} className="flex flex-col items-center min-w-[50px]">
                <span className="text-[10px] text-muted-foreground">{week.label}</span>
                <span className="text-lg font-bold text-foreground tabular-nums">{week.count}</span>
              </div>
            ))}
            <div className="flex flex-col items-center min-w-[50px] pl-2 border-l-2 border-primary/30">
              <span className="text-[10px] text-primary font-medium">Proj</span>
              <span className="text-lg font-bold text-primary tabular-nums">{weeklyData.projected}</span>
            </div>
          </div>
          
          {/* Divider */}
          <div className="h-10 w-px bg-border shrink-0" />
          
          {/* Stats inline */}
          <div className="flex items-center gap-4 shrink-0">
            <div className="flex items-center gap-2">
              <TrendingUp className="text-primary" size={16} />
              <div>
                <span className="text-[10px] text-muted-foreground block">Avg/Week</span>
                <span className="text-lg font-bold">{stats.avg_visits_per_week.toFixed(1)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <AlertCircle className="text-clay" size={16} />
              <div>
                <span className="text-[10px] text-muted-foreground block">Avg Missed</span>
                <span className="text-lg font-bold">{stats.avg_missed_last_6_weeks.toFixed(1)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="text-stone" size={16} />
              <div>
                <span className="text-[10px] text-muted-foreground block">Avg Late</span>
                <span className="text-lg font-bold">{stats.avg_late_notes_last_6_weeks.toFixed(1)}</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Weekly Progress Chart */}
      <Card className="p-6">
        <h3 className="font-heading text-xl font-semibold mb-4">
          Weekly Visit Progress (Last 12 Months)
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Weeks run Sunday through Saturday
        </p>
        {weeklyChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={weeklyChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="week" 
                tick={{ fontSize: 10 }}
                interval={3}
                tickFormatter={(val) => {
                  // Parse YYYY-MM-DD without timezone issues
                  const [year, month, day] = val.split('-').map(Number);
                  return `${month}/${day}`;
                }}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                labelFormatter={(val) => {
                  // Parse YYYY-MM-DD without timezone issues
                  const [year, month, day] = val.split('-').map(Number);
                  const date = new Date(year, month - 1, day);
                  return `Week ending Sat ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="visits" stroke="#1a365d" strokeWidth={2} name="Total Visits" dot={false} />
              <Line type="monotone" dataKey="missed" stroke="#ef4444" strokeWidth={2} name="Missed" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            No data available for chart
          </div>
        )}
      </Card>

      {/* Visits by Discipline */}
      <Card className="p-6">
        <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
          <Stethoscope size={20} />
          Visits by Discipline
        </h3>
        {disciplineData.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {disciplineData.map((item) => (
              <div key={item.name} className="p-4 bg-muted/30 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground">{item.name}</p>
                <p className="text-2xl font-bold mt-1">{item.count}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No discipline data available
          </div>
        )}
      </Card>

      {/* Agency Onboarding Platform */}
      <AgencyOnboarding agencyId={agencyId} onAgencyChange={fetchAgencyData} />

      {/* Required Credentials Section */}
      <Card className="p-6" data-testid="agency-required-credentials-card">
        <AgencyRequiredCredentials agencyId={agencyId} />
      </Card>

      {/* Custom Fields Section */}
      {(customFields.length > 0 || editingLayout) && (
        <Card className="p-6" data-testid="agency-custom-fields">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-semibold">Custom Fields</h3>
            {editingLayout && (
              <Button variant="outline" size="sm" onClick={() => setShowAddFieldModal(true)} data-testid="agency-add-field-btn">
                <Plus size={14} className="mr-1" /> Add Field
              </Button>
            )}
          </div>
          {customFields.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No custom fields yet. Click "Add Field" to create one.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customFields.map(field => (
                <div key={field.id} className="relative group">
                  {editingLayout && (
                    <button
                      onClick={() => handleRemoveCustomField(field.id)}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                      data-testid={`agency-remove-field-${field.id}`}
                    >
                      ×
                    </button>
                  )}
                  <p className="text-xs text-muted-foreground mb-1">{field.name}</p>
                  <Input
                    data-testid={`agency-custom-field-${field.field_key}`}
                    type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                    value={customData[field.field_key] || ''}
                    onChange={e => setCustomData(prev => ({ ...prev, [field.field_key]: e.target.value }))}
                    onBlur={e => saveCustomData(field.field_key, e.target.value)}
                    placeholder={field.placeholder || field.name}
                    className="h-8 text-sm"
                  />
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Unverified Invoices */}
      <Card className={`p-6 ${unverifiedInvoices.length > 0 ? 'border-orange-200 bg-orange-50/30' : ''}`}>
        <h3 className={`font-heading text-xl font-semibold mb-4 flex items-center gap-2 ${unverifiedInvoices.length > 0 ? 'text-orange-700' : ''}`}>
          <AlertCircle size={20} className={unverifiedInvoices.length > 0 ? 'text-orange-500' : 'text-muted-foreground'} />
          Unverified Invoices ({unverifiedInvoices.length})
        </h3>
        {unverifiedInvoices.length > 0 ? (
          <div className="overflow-auto rounded-lg border border-orange-200">
            <table className="w-full">
              <thead className="bg-orange-100/50">
                <tr>
                  <th className="text-left text-xs font-medium text-orange-700 uppercase tracking-wider p-3">
                    Invoice #
                  </th>
                  <th className="text-left text-xs font-medium text-orange-700 uppercase tracking-wider p-3">
                    Date
                  </th>
                  <th className="text-right text-xs font-medium text-orange-700 uppercase tracking-wider p-3">
                    Amount
                  </th>
                  <th className="text-left text-xs font-medium text-orange-700 uppercase tracking-wider p-3">
                    Notes
                  </th>
                  <th className="text-left text-xs font-medium text-orange-700 uppercase tracking-wider p-3">
                    Uploaded
                  </th>
                  <th className="text-right text-xs font-medium text-orange-700 uppercase tracking-wider p-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {unverifiedInvoices.map((invoice) => (
                  <tr key={invoice.id} className="border-t border-orange-200 hover:bg-orange-50">
                    <td className="p-3 font-mono text-sm">{invoice.invoice_number}</td>
                    <td className="p-3 text-sm">{formatDate(invoice.invoice_date)}</td>
                    <td className="p-3 text-sm text-right font-medium">{formatCurrency(invoice.amount)}</td>
                    <td className="p-3 text-sm text-muted-foreground max-w-[200px] truncate" title={invoice.verification_notes}>
                      {invoice.verification_notes || '-'}
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">{formatDate(invoice.uploaded_at)}</td>
                    <td className="p-3 text-right space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openNoteModal(invoice)}
                        data-testid={`note-invoice-${invoice.id}`}
                      >
                        <MessageSquare size={14} className="mr-1" />
                        Note
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openVerifyModal(invoice)}
                        data-testid={`verify-invoice-${invoice.id}`}
                      >
                        <CheckCircle2 size={14} className="mr-1" />
                        Verify
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No unverified invoices for this agency
          </div>
        )}
      </Card>

      {/* Verified Invoices History */}
      <Card className="p-6">
        <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
          <FileText size={20} />
          Invoice History ({verifiedInvoices.length})
        </h3>
        
        {verifiedInvoices.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No verified invoices found
          </div>
        ) : (
          <div className="overflow-auto rounded-lg border border-border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-3">
                    Invoice #
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-3">
                    Date
                  </th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider p-3">
                    Amount
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-3">
                    Verified By
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-3">
                    Verified On
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-3">
                    Notes
                  </th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider p-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {verifiedInvoices.map((invoice) => (
                  <tr key={invoice.id} className="border-t border-border hover:bg-muted/20">
                    <td className="p-3 font-mono text-sm">{invoice.invoice_number}</td>
                    <td className="p-3 text-sm">{formatDate(invoice.invoice_date)}</td>
                    <td className="p-3 text-sm text-right font-medium">{formatCurrency(invoice.amount)}</td>
                    <td className="p-3 text-sm text-muted-foreground">{resolveName(invoice.verified_by) || '-'}</td>
                    <td className="p-3 text-sm text-muted-foreground">{formatDate(invoice.verified_at)}</td>
                    <td className="p-3 text-sm text-muted-foreground max-w-[200px]">
                      {invoice.verification_notes ? (
                        <span className="flex items-start gap-1" title={invoice.verification_notes}>
                          <MessageSquare size={12} className="mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{invoice.verification_notes}</span>
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openNoteModal(invoice)}
                          data-testid={`edit-note-${invoice.id}`}
                        >
                          <Edit2 size={14} className="mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-orange-600 hover:text-orange-700"
                          onClick={() => handleUnverifyInvoice(invoice.id)}
                          data-testid={`unverify-${invoice.id}`}
                        >
                          <X size={14} className="mr-1" />
                          Unverify
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Verification Modal */}
      {verifyModal.open && verifyModal.invoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-lg">Verify Invoice</h2>
              <Button variant="ghost" size="sm" onClick={closeVerifyModal}>
                <X size={18} />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Invoice Details */}
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Invoice #</span>
                  <span className="font-mono font-medium">{verifyModal.invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium">{formatCurrency(verifyModal.invoice.amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Date</span>
                  <span>{formatDate(verifyModal.invoice.invoice_date)}</span>
                </div>
              </div>

              {/* Notes Input */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <MessageSquare size={14} />
                  Verification Notes (optional)
                </label>
                <textarea
                  value={verifyNotes}
                  onChange={(e) => setVerifyNotes(e.target.value)}
                  placeholder="Add any notes about this invoice verification..."
                  className="w-full min-h-[100px] p-3 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="verify-notes-input"
                />
              </div>
            </div>

            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={closeVerifyModal}>
                Cancel
              </Button>
              <Button 
                onClick={submitVerification} 
                disabled={submittingVerify}
                data-testid="submit-verify-btn"
              >
                {submittingVerify ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                ) : (
                  <CheckCircle2 size={16} className="mr-2" />
                )}
                Verify Invoice
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Note Modal (for adding notes without verifying) */}
      {noteModal.open && noteModal.invoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-lg">Add Note to Invoice</h2>
              <Button variant="ghost" size="sm" onClick={closeNoteModal}>
                <X size={18} />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Invoice Details */}
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Invoice #</span>
                  <span className="font-mono font-medium">{noteModal.invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium">{formatCurrency(noteModal.invoice.amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Date</span>
                  <span>{formatDate(noteModal.invoice.invoice_date)}</span>
                </div>
              </div>

              {/* Notes Input */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <MessageSquare size={14} />
                  Note
                </label>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note about this invoice..."
                  className="w-full min-h-[100px] p-3 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="invoice-note-input"
                />
              </div>
            </div>

            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={closeNoteModal}>
                Cancel
              </Button>
              <Button 
                onClick={submitNote} 
                disabled={submittingNote || !noteText.trim()}
                data-testid="submit-note-btn"
              >
                {submittingNote ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                ) : (
                  <MessageSquare size={16} className="mr-2" />
                )}
                Save Note
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Add Custom Field Modal */}
      <Dialog open={showAddFieldModal} onOpenChange={setShowAddFieldModal}>
        <DialogContent className="sm:max-w-md" data-testid="agency-add-field-modal">
          <DialogHeader>
            <DialogTitle>Add Custom Field</DialogTitle>
            <DialogDescription>Create a new field that will appear on this agency record.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Field Name *</Label>
              <Input
                data-testid="agency-add-field-name"
                value={newFieldForm.name}
                onChange={e => setNewFieldForm({ ...newFieldForm, name: e.target.value })}
                placeholder="e.g., Contract Number"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={newFieldForm.field_type} onValueChange={v => setNewFieldForm({ ...newFieldForm, field_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="boolean">Yes/No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Input
                  value={newFieldForm.description}
                  onChange={e => setNewFieldForm({ ...newFieldForm, description: e.target.value })}
                  placeholder="Brief description"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddFieldModal(false)}>Cancel</Button>
            <Button onClick={handleAddCustomField} data-testid="agency-add-field-submit">
              <Plus size={14} className="mr-2" /> Add Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}